// Warnings and result assembly (§5, §6). Pure: runs identically in the browser and in Node.

import type { Field, FieldResult, GoogleCut, Overall, Profile, ProfileResult, SerpSizeConfig, Severity, Status, Warning, WarningCode } from './types';
import { danglingConnector, findOccurrences, occurrenceShown } from './text';

export const SEVERITY: Record<WarningCode, Severity> = {
  BRAND_HIDDEN: 'high',
  KEYWORD_HIDDEN: 'high',
  DANGLING_END: 'medium',
  LOW_SHOWN: 'medium',
  FONT_SENSITIVE: 'medium',
  TIGHT: 'low',
};

/** Order codes are listed in, most severe first. */
export const WARNING_ORDER: WarningCode[] = ['BRAND_HIDDEN', 'KEYWORD_HIDDEN', 'FONT_SENSITIVE', 'DANGLING_END', 'LOW_SHOWN', 'TIGHT'];
export const FIELD_LEVEL: ReadonlySet<WarningCode> = new Set(['BRAND_HIDDEN', 'KEYWORD_HIDDEN', 'FONT_SENSITIVE']);

const STATUS_RANK: Record<Overall, number> = { fits: 0, tight: 1, cut: 2, error: 3 };

export function worst<T extends Overall>(statuses: T[]): T | 'fits' {
  let w: Overall = 'fits';
  for (const s of statuses) if (STATUS_RANK[s] > STATUS_RANK[w]) w = s;
  return w as T | 'fits';
}

export interface AssembleInput {
  url: string;
  field: Field;
  original: string;
  cut: GoogleCut;
  results: ProfileResult[];
  brand?: string;
  targetKeyword?: string;
  profiles: Map<string, Profile>;
  config: SerpSizeConfig;
}

function label(p: Profile | undefined, id: string): string {
  return p?.shortLabel ?? p?.label ?? id;
}

function fontName(font: string): string {
  const m = font.match(/\dpx(?:\/\S+)?\s+(.+)$/);
  return m ? m[1].split(',')[0].trim().replace(/^["']|["']$/g, '') : font;
}

function list(ids: string[], profiles: Map<string, Profile>): string {
  return ids.map(id => label(profiles.get(id), id)).join(', ');
}

/** Profile-level warnings (§5) for one result. */
export function profileWarnings(r: ProfileResult, config: SerpSizeConfig): { codes: WarningCode[]; dangling: string | null } {
  const codes: WarningCode[] = [];
  let dangling: string | null = null;
  if (r.notShownText) {
    dangling = danglingConnector(r.shownText, config.connectors);
    if (dangling) codes.push('DANGLING_END');
  }
  if (r.shownPct < config.lowShownPct) codes.push('LOW_SHOWN');
  if (r.status === 'tight') codes.push('TIGHT');
  return { codes, dangling };
}

/** Build a FieldResult from per-profile engine results, adding field-level and profile-level warnings. */
export function assembleField(input: AssembleInput): FieldResult {
  const { field, original, cut, results, profiles, config } = input;
  const warnings: Warning[] = [];
  const perProfile = new Map<string, Set<WarningCode>>();
  for (const r of results) perProfile.set(r.profile, new Set());

  const hiddenCheck = (code: 'BRAND_HIDDEN' | 'KEYWORD_HIDDEN', needle: string | undefined, what: string) => {
    if (!needle || !needle.trim()) return;
    const occ = findOccurrences(original, needle);
    if (occ.length === 0) return;
    const hiddenOn = results.filter(r => !occurrenceShown(occ, r.shownEnd)).map(r => r.profile);
    if (hiddenOn.length === 0) return;
    for (const id of hiddenOn) perProfile.get(id)!.add(code);
    const where = hiddenOn.length === results.length ? 'on every device' : `on ${list(hiddenOn, profiles)}`;
    warnings.push({ code, severity: SEVERITY[code], field, profiles: hiddenOn, detail: needle.trim(), message: `${what} "${needle.trim()}" is not fully shown ${where}.` });
  };
  hiddenCheck('BRAND_HIDDEN', input.brand, 'Brand');
  hiddenCheck('KEYWORD_HIDDEN', input.targetKeyword, 'Target keyword');

  const sensitive = results.filter(r => r.twinShownEnd !== undefined && r.twinShownEnd !== r.shownEnd);
  if (sensitive.length) {
    for (const r of sensitive) perProfile.get(r.profile)!.add('FONT_SENSITIVE');
    const first = sensitive[0];
    const p = profiles.get(first.profile);
    const twin = first.twinProfile ? profiles.get(first.twinProfile) : undefined;
    const detail = `${label(p, first.profile)} vs ${twin ? fontName(twin[field].font) : 'twin font'}`;
    warnings.push({
      code: 'FONT_SENSITIVE',
      severity: SEVERITY.FONT_SENSITIVE,
      field,
      profiles: sensitive.map(r => r.profile),
      detail,
      message: `The shown text changes with the font on ${list(sensitive.map(r => r.profile), profiles)}; Google shows different fonts to different visitors.`,
    });
  }

  const profileLevel = new Map<WarningCode, { ids: string[]; details: string[] }>();
  for (const r of results) {
    const { codes, dangling } = profileWarnings(r, config);
    for (const c of codes) {
      perProfile.get(r.profile)!.add(c);
      let e = profileLevel.get(c);
      if (!e) profileLevel.set(c, (e = { ids: [], details: [] }));
      e.ids.push(r.profile);
      if (c === 'DANGLING_END' && dangling && !e.details.includes(dangling)) e.details.push(dangling);
    }
  }
  for (const [code, { ids, details }] of profileLevel) {
    const on = ids.length === results.length ? 'on every device' : `on ${list(ids, profiles)}`;
    let message: string;
    let detail: string | undefined;
    if (code === 'DANGLING_END') {
      detail = details.join(', ');
      message = `The shown text ends on "${detail}" ${on}.`;
    } else if (code === 'LOW_SHOWN') {
      message = `Fewer than ${config.lowShownPct}% of the words are shown ${on}.`;
    } else {
      message = `Fits, but with little room left ${on}.`;
    }
    warnings.push({ code, severity: SEVERITY[code], field, profiles: ids, detail, message });
  }
  warnings.sort((a, b) => WARNING_ORDER.indexOf(a.code) - WARNING_ORDER.indexOf(b.code));

  const finalResults = results.map(r => ({ ...r, warnings: WARNING_ORDER.filter(c => perProfile.get(r.profile)!.has(c)) }));
  const overall = worst(finalResults.map(r => r.status)) as Status;
  const googleCut: FieldResult['googleCut'] =
    field === 'title'
      ? { cut: cut.cut, sent: cut.sent, widthPx: round1(cut.widthPx ?? 0), limitPx: cut.limitPx }
      : { cut: cut.cut, sent: cut.sent, chars: cut.chars, charCap: cut.charCap };
  if (cut.midWord) googleCut.midWord = true;
  return { url: input.url, field, original, overall, googleCut, results: finalResults, warnings };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function errorField(url: string, field: Field, error: string): FieldResult {
  return { url, field, original: '', overall: 'error', googleCut: { cut: false, sent: '' }, results: [], warnings: [], error };
}
